import React, { useEffect, useState } from "react";
import axios from "axios";

const SeoSettings = () => {
  const [selectedPage, setSelectedPage] = useState("home");
  const accessToken = localStorage.getItem("authToken");
  const [formData, setFormData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    metaRedirect: "",
    metaCanonical: "",
    metaHtmlHead: "",
    metaHtmlFooter: "",
    ogImage: null,
  });

  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const pages = [
    { value: "home", label: "Home" },
    { value: "about-us", label: "About Us" },
    { value: "contact-us", label: "Contact Us" },
    { value: "properties", label: "Properties" },
    { value: "listings", label: "Listings" },
    { value: "services", label: "Services" },
    { value: "list-with-us", label: "List With Us" },
    { value: "blog", label: "Blog" },
  ];

  useEffect(() => {
    const fetchSeoData = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          `https://365luxuryhomes.com/api/seo-settings/${selectedPage}`
        );
        const seoData = response.data;

        setFormData({
          metaTitle: seoData.metaTitle || "",
          metaDescription: seoData.metaDescription || "",
          metaKeywords: seoData.metaKeywords || "",
          metaRedirect: seoData.metaRedirect || "",
          metaCanonical: seoData.metaCanonical || "",
          metaHtmlHead: seoData.metaHtmlHead || "",
          metaHtmlFooter: seoData.metaHtmlFooter || "",
          ogImage: seoData.ogImage || null,
        });
        setPreviewImage(seoData.ogImage || null);
      } catch (error) {
        console.error("Error fetching SEO data:", error);
        alert("No data found for this page!");
      } finally {
        setLoading(false);
      }
    };

    fetchSeoData();
  }, [selectedPage]);

  const handlePageChange = (e) => {
    setSelectedPage(e.target.value);
    setFormData({
      metaTitle: "",
      metaDescription: "",
      metaKeywords: "",
      metaRedirect: "",
      metaCanonical: "",
      metaHtmlHead: "",
      metaHtmlFooter: "",
      ogImage: null,
    });
    setPreviewImage(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, ogImage: file });
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = new FormData();
      form.append("ogImage", formData.ogImage);
      form.append("metaTitle", formData.metaTitle);
      form.append("metaDescription", formData.metaDescription);
      form.append("metaKeywords", formData.metaKeywords);
      form.append("metaRedirect", formData.metaRedirect);
      form.append("metaCanonical", formData.metaCanonical);
      form.append("metaHtmlHead", formData.metaHtmlHead);
      form.append("metaHtmlFooter", formData.metaHtmlFooter);
      form.append("page", selectedPage);

      const response = await axios.post(
        "https://365luxuryhomes.com/api/seo-settings",
        form,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert(response.data.message);
    } catch (error) {
      console.error("Error updating SEO settings:", error);
      alert("Failed to update SEO settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <h1 className="admin-page-title">SEO Settings</h1>
      <p className="admin-page-desc">Manage meta tags and Open Graph per page.</p>

      <div className="admin-card max-w-4xl">
        <div className="mb-6">
          <label className="admin-label">Select Page</label>
          <select
            value={selectedPage}
            onChange={handlePageChange}
            className="admin-input"
          >
            {pages.map((page) => (
              <option key={page.value} value={page.value}>
                {page.label}
              </option>
            ))}
          </select>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="admin-label">Meta Title</label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleInputChange}
                className="admin-input"
                required
              />
            </div>
            <div>
              <label className="admin-label">Meta Description</label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                className="admin-input"
                rows={4}
                required
              />
            </div>
            <div>
              <label className="admin-label">Meta Keywords</label>
              <input
                type="text"
                name="metaKeywords"
                value={formData.metaKeywords}
                onChange={handleInputChange}
                className="admin-input"
                required
              />
            </div>
            <div>
              <label className="admin-label">Meta Redirect</label>
              <input
                type="url"
                name="metaRedirect"
                value={formData.metaRedirect}
                onChange={handleInputChange}
                className="admin-input"
              />
            </div>
            <div>
              <label className="admin-label">Meta Canonical</label>
              <input
                type="url"
                name="metaCanonical"
                value={formData.metaCanonical}
                onChange={handleInputChange}
                className="admin-input"
                required
              />
            </div>
            <div>
              <label className="admin-label">Meta HTML Head</label>
              <textarea
                name="metaHtmlHead"
                value={formData.metaHtmlHead}
                onChange={handleInputChange}
                className="admin-input"
                rows={4}
              />
            </div>
            <div>
              <label className="admin-label">Meta HTML Footer</label>
              <textarea
                name="metaHtmlFooter"
                value={formData.metaHtmlFooter}
                onChange={handleInputChange}
                className="admin-input"
                rows={4}
              />
            </div>
            <div>
              <label className="admin-label">OG Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="admin-input"
              />
              {previewImage && (
                <img
                  src={previewImage}
                  alt="OG Preview"
                  className="mt-3 w-36 h-36 object-cover rounded-admin border border-base-300"
                />
              )}
              <p className="text-base-content/60 text-sm mt-1.5">
                Supported: jpeg, png, webp. Max size: 10 MB.
              </p>
            </div>
          </div>
          <div className="pt-2">
            <button
              type="submit"
              className="btn btn-primary rounded-admin"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SeoSettings;
